/**
 * Staun Product Card — Video hover + AJAX quick add
 * - Plays product video on card hover, pauses on leave
 * - Submits add-to-cart via fetch, keeps user on page
 * - Dispatches cart events so Dawn's cart drawer/notification updates
 */

'use strict';

(() => {
  const cards = document.querySelectorAll('[data-staun-product-card]');
  if (!cards.length) return;

  /**
   * Hover play/pause for card videos.
   */
  cards.forEach((card) => {
    const video = card.querySelector('[data-staun-product-video]');
    if (!video) return;

    card.addEventListener('mouseenter', () => {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { /* autoplay blocked — harmless */ });
      }
    });

    card.addEventListener('mouseleave', () => {
      video.pause();
      video.currentTime = 0;
    });
  });

  /**
   * Intercept quick-add form submits and post to /cart/add.js.
   * On success, refresh Dawn's cart UI.
   */
  const forms = document.querySelectorAll('[data-staun-quick-add-form]');

  const refreshCartUI = async () => {
    /* Pull updated sections from Shopify so Dawn's cart components re-render */
    const sectionsToFetch = [];
    const cartNotification = document.querySelector('cart-notification');
    const cartDrawer = document.querySelector('cart-drawer');
    const cartIconBubble = document.getElementById('cart-icon-bubble');

    if (cartNotification) sectionsToFetch.push(cartNotification.getSectionsToRender?.().map(s => s.id));
    if (cartDrawer) sectionsToFetch.push(cartDrawer.getSectionsToRender?.().map(s => s.id));

    /* Flatten and dedupe */
    const unique = [...new Set(sectionsToFetch.flat().filter(Boolean))];
    if (!unique.length) {
      /* Fallback: refresh cart icon bubble only */
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-icon-bubble`);
        const data = await res.json();
        if (cartIconBubble && data['cart-icon-bubble']) {
          const parsed = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
          const newBubble = parsed.querySelector('#cart-icon-bubble');
          if (newBubble) cartIconBubble.innerHTML = newBubble.innerHTML;
        }
      } catch (_) { /* ignore */ }
      return;
    }

    try {
      const url = `${window.Shopify?.routes?.root || '/'}?sections=${unique.join(',')}`;
      const res = await fetch(url);
      const data = await res.json();
      if (cartNotification) cartNotification.renderContents?.({ sections: data });
      if (cartDrawer) cartDrawer.renderContents?.({ sections: data });
    } catch (_) { /* ignore */ }
  };

  forms.forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const btn = form.querySelector('.staun-product-card__add-btn');
      if (btn) {
        btn.disabled = true;
        btn.dataset.originalText = btn.textContent;
        btn.textContent = 'Adding...';
      }

      try {
        const formData = new FormData(form);
        /* Ask Shopify to include the cart drawer and notification sections */
        const sections = ['cart-drawer', 'cart-notification', 'cart-icon-bubble']
          .filter((id) => document.getElementById(id) || document.querySelector(id))
          .join(',');
        if (sections) formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);

        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/javascript' },
          body: formData,
        });

        if (!res.ok) throw new Error('Add to cart failed');
        const data = await res.json();

        /* Open cart drawer or show notification, whichever the theme uses */
        const cartDrawer = document.querySelector('cart-drawer');
        const cartNotification = document.querySelector('cart-notification');

        if (cartNotification && typeof cartNotification.renderContents === 'function') {
          cartNotification.renderContents(data);
        } else if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data);
        } else {
          /* Fallback: refresh cart UI manually */
          await refreshCartUI();
        }

        /* Broadcast so anything listening (like cart count badges) can update */
        document.dispatchEvent(new CustomEvent('staun:cart:added', { detail: data }));
      } catch (err) {
        /* Surface a minimal error — avoid breaking the page */
        console.error('[staun] Quick add failed:', err);
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.textContent = btn.dataset.originalText || 'Add to cart';
        }
      }
    });
  });
})();
